<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      class="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
      :class="open ? 'bg-white/10' : ''"
      :aria-label="currentLabel"
      @click="open = !open"
    >
      <span class="w-6 h-6 flex items-center justify-center rounded bg-purple-600 text-xs font-medium uppercase">
        {{ locale }}
      </span>
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    <div
      v-if="open"
      class="absolute right-0 mt-1 w-32 bg-[#09060f] border border-white/15 rounded-md shadow-lg shadow-black/70 py-1 z-50"
    >
      <button
        v-for="loc in locales"
        :key="loc.code"
        type="button"
        class="block w-full text-left px-4 py-2 text-sm transition-colors"
        :class="locale === loc.code ? 'bg-purple-400/30 text-purple-200 font-medium' : 'text-gray-400 hover:bg-purple-500/20 hover:text-purple-300'"
        @click="selectLocale(loc.code)"
      >
        {{ loc.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { supportedLocales, setLocaleStorage, type SupportedLocale } from '@/i18n'

const { locale } = useI18n()
const open = ref(false)
const rootRef = ref<HTMLElement | null>(null)

const localeLabels: Record<SupportedLocale, string> = {
  en: 'English',
  vi: 'Vietnamese',
}

const locales = computed(() =>
  supportedLocales.map((code) => ({ code, label: localeLabels[code] }))
)

const currentLabel = computed(() => localeLabels[locale.value as SupportedLocale] ?? locale.value)

function selectLocale(code: string) {
  locale.value = code
  setLocaleStorage(code)
  if (typeof document !== 'undefined') document.documentElement.lang = code
  open.value = false
}

function handleClickOutside(e: MouseEvent) {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) open.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
